import {
  MIRA_CROP_CONFIRMED,
  MIRA_IMAGES_KEY,
  MIRA_MAX_IMAGES,
  MIRA_MAX_IMAGE_BYTES,
  type MiraImage,
  type MiraImageMediaType,
} from '@lensmira/domain'

/**
 * `AI-004`'s crop, as pure functions, and the one place a sendable image is minted.
 *
 * > `AI-004` `MUST` No customer name, contact detail or identifier is sent to a model. Prescription
 * > images are stripped of identifying regions before transmission where possible, and the payload
 * > minimised to what the task needs.
 *
 * A prescription slip has the patient's name written across the top of it, and the two payload
 * gates on the server read keys: neither can see a name in a photograph. "Where possible" is the
 * appendix's own hedge and this is what it was decided to mean. The panel shows the photograph, the
 * shopkeeper drags a box over the values, and only what is inside the box is encoded and uploaded.
 *
 * **The crop is the only route to a sendable image, and that is membership rather than a check.**
 * {@link confirmCrop} is the one writer to a module-private `WeakSet` and {@link imagePayload}
 * refuses anything that is not in it, the same pattern and the same reason as `DECIDED` in
 * `acceptance.ts`: a hand-built `{ mediaType, data, cropConfirmed }` typechecks perfectly and would
 * put an uncropped photograph of somebody's name in front of a sub-processor. A type brand is the
 * tidier fix and an `as` cast defeats it. `CLAUDE.md` asks for impossible rather than discouraged.
 *
 * **No DOM in this file.** The crop geometry is fractions of the image, so it needs no element and
 * no layout, which is also the only way it can be tested: jsdom has no layout, no canvas and no
 * image decoding. The encode itself is the caller's, injected as {@link CropEncoder} and
 * implemented for a real browser in `browser-images.ts`.
 */

// -----------------------------------------------------------------------------------------------
// The crop, as fractions of the photograph
// -----------------------------------------------------------------------------------------------

/**
 * The region that is sent, as fractions of the image from its top left corner.
 *
 * Fractions and not pixels: the person drags on a photograph scaled to fit a tablet's screen, and
 * the same box has to mean the same region of a 12-megapixel original. Converting once, in
 * {@link cropPixels}, is also what keeps the drag independent of how the preview was laid out.
 */
export interface CropBox {
  readonly left: number
  readonly top: number
  readonly right: number
  readonly bottom: number
}

export const WHOLE_IMAGE: CropBox = { left: 0, top: 0, right: 1, bottom: 1 }

/**
 * The smallest box that may be sent, as a fraction of each edge.
 *
 * **A number chosen here, not in the spec.** A box of a few pixels is a mis-aimed drag rather than
 * a crop, and the model would be sent a smudge and would answer "illegible", which costs a credit
 * and reads to a shopkeeper as Mira being useless. Five per cent of a 1600 px edge is 80 px, which
 * is about one character, so anything smaller cannot be a prescription value either.
 */
export const MIN_CROP_FRACTION = 0.05

export interface Point {
  readonly x: number
  readonly y: number
}

/** The part of a `DOMRect` a drag needs. Taken as data so this file needs no DOM. */
export interface Bounds {
  readonly left: number
  readonly top: number
  readonly width: number
  readonly height: number
}

const clamp01 = (value: number): number => (value < 0 ? 0 : value > 1 ? 1 : value)

const round3 = (value: number): number => Math.round(value * 1000) / 1000

/**
 * The box two drag points describe, in whichever order they were made.
 *
 * Returns null for a drag too small to be a crop ({@link MIN_CROP_FRACTION}) and for an element
 * with no size, which is what a zero-width `getBoundingClientRect` means: the caller keeps the box
 * it had rather than replacing it with nonsense, because a tap that registers as a zero-length drag
 * is the ordinary accident on a touchscreen.
 */
export function cropFromDrag(from: Point, to: Point, bounds: Bounds): CropBox | null {
  if (bounds.width <= 0 || bounds.height <= 0) return null
  const xs = [from.x, to.x]
    .map((x) => clamp01((x - bounds.left) / bounds.width))
    .sort((a, b) => a - b)
  const ys = [from.y, to.y]
    .map((y) => clamp01((y - bounds.top) / bounds.height))
    .sort((a, b) => a - b)
  const [left, right] = xs as [number, number]
  const [top, bottom] = ys as [number, number]
  if (right - left < MIN_CROP_FRACTION || bottom - top < MIN_CROP_FRACTION) return null
  return { left: round3(left), top: round3(top), right: round3(right), bottom: round3(bottom) }
}

export const CROP_EDGES = ['left', 'top', 'right', 'bottom'] as const
export type CropEdge = (typeof CROP_EDGES)[number]

/**
 * One edge moved to a new fraction, or the box unchanged where that would invert or over-shrink it.
 *
 * This is the keyboard and precision route to the same box the drag produces (`KB-02`: every
 * control at the counter works from the keyboard). It refuses rather than clamping to the minimum,
 * so a held arrow key stops at a box that is still a crop instead of collapsing to a sliver.
 */
export function moveCropEdge(box: CropBox, edge: CropEdge, fraction: number): CropBox {
  const next = { ...box, [edge]: round3(clamp01(fraction)) }
  if (next.right - next.left < MIN_CROP_FRACTION) return box
  if (next.bottom - next.top < MIN_CROP_FRACTION) return box
  return next
}

/**
 * Whether a person has actually chosen a region.
 *
 * The box starts as the whole photograph, so without this the confirm button would be a press that
 * changes nothing and `AI-004`'s step would be satisfied by a reflex. What this can guarantee is
 * that somebody looked at the photograph and said which part of it is sent; it cannot guarantee the
 * name is outside the box, which is why `MIRA_CROP_CONFIRMED` calls itself an attestation.
 */
export function cropChosen(box: CropBox): boolean {
  return box.left > 0 || box.top > 0 || box.right < 1 || box.bottom < 1
}

/**
 * The most of a photograph a confirmed crop may keep, as a fraction of its area.
 *
 * **A number chosen here, not in the spec, and the one to put in front of the pilot shop.**
 * `AI-004` says prescription images are "stripped of identifying regions before transmission where
 * possible", and {@link cropChosen} only asks that an edge moved: one press of ArrowUp on the left
 * edge makes the box ninety-nine per cent of the photograph and mints the confirmation, with the
 * patient's name still in the top band, which is the band the panel's own hint
 * (`mira.image.privacy`) tells the shopkeeper to keep outside the box. The reflex press this gate
 * exists to prevent was still one keystroke away.
 *
 * Nine tenths because a box that keeps more than that has excluded a border rather than a region,
 * and the name and telephone number across the top of a Pakistani prescription slip are more than a
 * tenth of it. It is a floor on carefulness and not a measurement: a shopkeeper who has already
 * framed the photograph tightly on the values is refused by it and has to say so, which is the cost
 * and the reason to measure it against real slips before it hardens.
 */
export const MAX_CROP_AREA_FRACTION = 0.9

/** The same number as a percentage, for the sentence the panel shows. */
export const MAX_CROP_AREA_PERCENT = Math.round(MAX_CROP_AREA_FRACTION * 100)

/** Whether the box keeps so much of the photograph that nothing was really excluded. */
export function cropKeepsTooMuch(box: CropBox): boolean {
  return (box.right - box.left) * (box.bottom - box.top) > MAX_CROP_AREA_FRACTION
}

export interface PixelRect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

/** The box in the original's own pixels, which is what a canvas `drawImage` takes. */
export function cropPixels(box: CropBox, naturalWidth: number, naturalHeight: number): PixelRect {
  const x = Math.round(box.left * naturalWidth)
  const y = Math.round(box.top * naturalHeight)
  return {
    x,
    y,
    width: Math.max(1, Math.round(box.right * naturalWidth) - x),
    height: Math.max(1, Math.round(box.bottom * naturalHeight) - y),
  }
}

/**
 * The longest edge a cropped image is scaled down to.
 *
 * **A number chosen here, not in the spec and not read from a provider's documentation.** It exists
 * so the encoder does not have to drop JPEG quality through the floor to reach
 * `MIRA_MAX_IMAGE_BYTES` from a 12-megapixel phone photograph, which would blur the digits the
 * whole feature is about. At 1600 px across a prescription slip a handwritten character is some
 * tens of pixels tall, which is legible, and a document photograph at that size and the quality
 * ladder below lands well inside the byte ceiling. Measure it against real slips at the pilot.
 */
export const MIRA_MAX_EDGE_PX = 1600

/** Scaled to fit inside a square of `maxEdge`, keeping the aspect ratio. Never scaled up. */
export function scaleToFit(
  width: number,
  height: number,
  maxEdge: number,
): { readonly width: number; readonly height: number } {
  const longest = Math.max(width, height)
  if (longest <= maxEdge) return { width, height }
  const factor = maxEdge / longest
  return {
    width: Math.max(1, Math.round(width * factor)),
    height: Math.max(1, Math.round(height * factor)),
  }
}

/**
 * The JPEG qualities tried, in order, until one fits `MIRA_MAX_IMAGE_BYTES`.
 *
 * **Chosen here.** 0.85 is where a document photograph stops looking re-compressed; the rest are a
 * ladder for a crop that is still too large, which is a big crop of a very large original. Below
 * 0.4 a thin pen stroke starts to break up, so the encoder refuses rather than sending something a
 * model will misread: a wrong sphere that looks plausible is the failure `AI-001` is written about.
 */
export const JPEG_QUALITIES: readonly number[] = [0.85, 0.7, 0.55, 0.4]

// -----------------------------------------------------------------------------------------------
// The mint: AI-004's step cannot be skipped
// -----------------------------------------------------------------------------------------------

/**
 * Every image {@link confirmCrop} has minted, and nothing else.
 *
 * Keyed on identity, so a structured clone, a spread, and a round trip through the write queue's
 * IndexedDB are all refused. There is no path where the panel needs any of those to work: an image
 * is cropped and sent in the same panel session, and a crop that survived a reload is a crop whose
 * photograph nobody is looking at any more.
 */
const CROPPED = new WeakSet<MiraImage>()

/** `AI-004`'s step, attempted around rather than through. */
export class NotCropped extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NotCropped'
  }
}

/** What a real browser does with a file and a box. Injected, because jsdom can do none of it. */
export type CropEncoder = (
  file: File,
  box: CropBox,
) => Promise<{ readonly mediaType: MiraImageMediaType; readonly data: string }>

/**
 * The one place a sendable image is made. Every `return` goes through the set.
 *
 * Takes the encoder rather than importing one so that a test's fake encoder cannot mint: the fake
 * returns bytes, this function decides they are cropped. An untouched box is refused here and not
 * only in the screen, for the reason `composeAnswer` throws as well as disabling a button.
 */
export async function confirmCrop(
  encode: CropEncoder,
  file: File,
  box: CropBox,
): Promise<MiraImage> {
  if (!cropChosen(box)) {
    throw new NotCropped(
      'No region was chosen on this photograph. AI-004 sends only the part a person drew a box ' +
        'around, because the name written at the top of a prescription slip is not something any ' +
        'payload check can see.',
    )
  }
  if (cropKeepsTooMuch(box)) {
    throw new NotCropped(
      `This box keeps more than ${String(MAX_CROP_AREA_PERCENT)} per cent of the photograph, so ` +
        'nothing was really left out of it. AI-004 asks for the identifying regions to be stripped ' +
        'where possible, and a box an edge inside the frame is the reflex press that step exists ' +
        'to prevent.',
    )
  }
  const encoded = await encode(file, box)
  const image: MiraImage = {
    mediaType: encoded.mediaType,
    data: encoded.data,
    cropConfirmed: MIRA_CROP_CONFIRMED,
  }
  CROPPED.add(image)
  return image
}

/**
 * The `images` half of a vision task's payload, from images this module minted.
 *
 * Throws rather than filtering. A payload quietly missing the photograph somebody meant to send is
 * a credit spent on a question about nothing, and the caller is code.
 */
export function imagePayload(images: readonly MiraImage[]): Record<string, unknown> {
  if (images.length === 0) {
    throw new NotCropped('A vision task is asked with at least one photograph.')
  }
  if (images.length > MIRA_MAX_IMAGES) {
    throw new NotCropped(
      `${String(images.length)} photographs: at most ${String(MIRA_MAX_IMAGES)} are sent, which is what a shop's connection can carry.`,
    )
  }
  for (const image of images) {
    if (!CROPPED.has(image)) {
      throw new NotCropped(
        'This image did not come from confirmCrop, so nobody chose what in it is sent. AI-004 ' +
          'strips the identifying regions of a photograph by cropping it before upload.',
      )
    }
  }
  return { [MIRA_IMAGES_KEY]: images }
}

/** For a screen that wants to say how large a photograph is. Base64 costs a third on top. */
export const MAX_IMAGE_KB = Math.round(MIRA_MAX_IMAGE_BYTES / 1000)

/**
 * What the file picker offers, which is **not** `MIRA_IMAGE_MEDIA_TYPES`.
 *
 * That list is the contract for what is *sent*, and it holds one entry because the canvas re-encode
 * emits JPEG. Using it as the input filter was a real bug: it hid every PNG from the picker, and a
 * PNG is what an Android screenshot and a spreadsheet export of a supplier price list both are, so
 * `AI-012` was the task it hurt most. `createImageBitmap` decodes all three of these, and the crop
 * re-encode turns whichever arrives into the one type that is sent.
 */
export const MIRA_PICKER_ACCEPT = 'image/jpeg,image/png,image/webp'
